/**
 * This file has a sister in the marcom project
 * https://github.com/joylifeinc/marcom/blob/master/src/hooks/useGeoLocale.ts
 */

import type { TranslationsByNamespace } from '@locales/generated';
import type { GetTranslation } from '@shared/core';
import { getLocalStorage, useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEffect } from 'react';
import { validCountryAlpha2Code } from './useGeoLocale/countries';
import { withWindow } from '@shared/utils/withWindow';

import { switchLanguage } from '@shared/core/i18n/switchLanguage';
import { resolveSupportedLocale } from '@shared/core/i18n/utils';
import { getEnabledGuestLanguages, isAdminRoutePath, readGuestLanguageCookie, removeGuestLanguageCookie } from '@shared/core/i18n/guestLanguage';
import { EventType as GeneratedEventType } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { isCurrencyCodeType } from '@shared/utils/currency/currency.constants';

export const CULTURE_PARAMS = ['culture', 'cul'];
export const CURRENCY_PARAMS = ['currency', 'cur'];
// 'lng' intentionally excluded — collides with longitude URL param used by
// hotel/accommodations search (see JW-47). Short-form language alias is 'l'.
export const LANGUAGE_PARAMS = ['language', 'lang', 'l'];

export enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD',
  NZD = 'NZD',
  SGD = 'SGD',
}

const CURRENCY_CODES = new Set<CurrencyCode>(Object.values(CurrencyCode));

export const isCurrencyCode = (value: string): value is CurrencyCode => {
  return CURRENCY_CODES.has(value as CurrencyCode);
};

export const asCurrencyCode = (value: string | undefined | null): CurrencyCode | undefined => {
  return value && isCurrencyCode(value) ? value : undefined;
};

export enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  NZ = 'NZ',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  IE = 'IE',
  JP = 'JP', // Japan's ISO 3166-1 alpha-2 country code
  US = 'US',
  SG = 'SG',
}

export enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja',
}

const findMatchingEnumValue = <T>(enumObj: Record<string, T>, key?: string): T | undefined => {
  if (!key) {
    return undefined;
  }

  const actualKey = Object.keys(enumObj).find(val => val?.toLowerCase() === key.toLowerCase());
  return actualKey ? enumObj[actualKey] : undefined;
};

type GeoBasedLocalePayload = Readonly<{
  culture: CultureCode;
  currency: CurrencyCodeType;
  language: LanguageCode;
}>;

type LocaleParamKey = 'culture' | 'currency' | 'language';

const PARAM_ALIASES: Record<LocaleParamKey, readonly string[]> = {
  culture: CULTURE_PARAMS,
  currency: CURRENCY_PARAMS,
  language: LANGUAGE_PARAMS,
};

const DefaultLocalePayload: GeoBasedLocalePayload = {
  currency: CurrencyCode.USD,
  culture: CultureCode.US,
  language: LanguageCode.en,
};

/**
 * ORDER IS LOAD-BEARING for any input that matches on language alone.
 *
 * `findClosestMatchingLocale` keeps the FIRST maximum, so whichever row for a
 * language appears earliest wins a bare code ("fr") or a region with no row of
 * its own ("fr-BE"). {CA, CAD, fr} used to sit ahead of {FR, EUR, fr}, which
 * served Belgian, Swiss and Luxembourgish visitors Canadian French and quoted
 * them in CAD — and because fr-CA is a registry member, `resolveSupportedLocale`
 * returned it unchanged instead of collapsing it to a sibling, so the wrong
 * dialect reached i18n.changeLanguage too.
 *
 * Keep each language's PRIMARY market first. Exact culture+language matches are
 * unaffected either way (they score 2 and win from any position); it is only the
 * language-only fallback that depends on this.
 */
const defaultLocales: ReadonlyArray<GeoBasedLocalePayload> = [
  DefaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de,
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it,
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.USD, // USD is the default currency for US, regardless of the language
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.IE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.JP,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja,
  },
  {
    culture: CultureCode.NZ,
    currency: CurrencyCode.NZD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.SG,
    currency: CurrencyCode.SGD,
    language: LanguageCode.en,
  },
];

export const findClosestMatchingLocale = (
  payload?: Partial<{
    culture: string;
    currency: string;
    language: string;
  }>,
  options?: { preferLanguage?: boolean },
): GeoBasedLocalePayload | undefined => {
  if (!payload) {
    return undefined;
  }

  // handle extended language codes (eg. en-AU, es-US)
  const [languageCode, cultureCode] = payload.language?.split('-') ?? [];

  const culture = findMatchingEnumValue(CultureCode, payload.culture ?? cultureCode);
  const normalizedCurrency = payload.currency?.toUpperCase();
  const currency = isCurrencyCodeType(normalizedCurrency) ? normalizedCurrency : findMatchingEnumValue(CurrencyCode, normalizedCurrency);
  const language = findMatchingEnumValue(LanguageCode, languageCode);

  // Skip processing if no valid parameters found
  if (!culture && !currency && !language) {
    return undefined;
  }

  const countMatchingProperties = (locale: GeoBasedLocalePayload): number => {
    // Prefer language 2:1 for display so `it-US` resolves to `it-IT` instead of `en-US`, but keep culture/currency matching 1:1 so pricing still follows US/USD.
    const languageWeight = options?.preferLanguage ? 2 : 1;
    return (locale.language === language ? languageWeight : 0) + (locale.culture === culture ? 1 : 0);
  };

  // Find the locale from defaultLocales that has the most matching culture/language properties
  const bestMatch = defaultLocales.reduce((bestMatch, locale) => {
    const currentScore = countMatchingProperties(locale);
    const bestScore = bestMatch ? countMatchingProperties(bestMatch) : 0;

    return currentScore > bestScore ? locale : bestMatch;
  });

  // If we have a valid currency from the payload, use it as an override; otherwise use the matched locale's currency
  return bestMatch ? { ...bestMatch, currency: currency || bestMatch.currency } : undefined;
};

export const constructLanguageCodeIso15897 = (language: LanguageCode, culture: CultureCode): string => {
  return `${language}-${culture}`;
};

const resolveLocaleWithOverlay = (partial: Partial<GeoBasedLocalePayload>): GeoBasedLocalePayload | undefined => {
  const resolved = findClosestMatchingLocale(partial);
  if (!resolved) return undefined;
  return {
    culture: partial.culture ?? resolved.culture,
    currency: partial.currency ?? resolved.currency,
    language: partial.language ?? resolved.language,
  };
};

export const updateLocaleLocalStorage = (updatedLocale: Partial<GeoBasedLocalePayload>) => {
  const locale = resolveLocaleWithOverlay(updatedLocale);
  if (!locale) return;
  getLocalStorage().setItem('joyLocale', JSON.stringify(locale));
};

export const updateLocaleSearchParams = (locale: Partial<Record<LocaleParamKey, string>>, options?: Readonly<{ addParam?: LocaleParamKey }>) => {
  const url = withWindow(global => new URL(global.location.href), undefined);
  if (!url) {
    return;
  }

  const { searchParams } = url;
  let didUpdate = false;

  for (const key of Object.keys(locale) as LocaleParamKey[]) {
    const value = locale[key];
    if (!value) continue;

    const aliases = PARAM_ALIASES[key];
    const paramToSet = aliases.find(a => searchParams.has(a)) ?? (options?.addParam === key ? aliases[0] : undefined);

    if (paramToSet) {
      searchParams.set(paramToSet, value);
      didUpdate = true;
    }
  }

  if (didUpdate) {
    withWindow(global => global.history.replaceState({}, '', url.toString()));
  }
};

const getLocallyStoredGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  if (!storedLocale) {
    return undefined;
  }

  try {
    return resolveLocaleWithOverlay(JSON.parse(storedLocale) as Partial<GeoBasedLocalePayload>);
  } catch (error) {
    addError(error as Error, {});
  }
  return undefined;
};

const getBrowserGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const browserLanguage = withWindow(global => global.navigator.language, undefined);
  if (!browserLanguage) return undefined;
  // Prefer language so `it-US` seeds `it` (not `en`) — otherwise an empty-storage
  // currency pick silently flips the UI language on mixed-locale users.
  return findClosestMatchingLocale({ language: browserLanguage }, { preferLanguage: true });
};

const useSearchParamsGeoLocale = (): { locale: GeoBasedLocalePayload | undefined; hasRegionSignal: boolean } => {
  const { getValueString } = useQueryParamHelper();

  const culture = getValueString(CULTURE_PARAMS);
  const currency = getValueString(CURRENCY_PARAMS);
  const language = getValueString(LANGUAGE_PARAMS);

  return {
    locale: findClosestMatchingLocale({ culture, currency, language }),
    hasRegionSignal: Boolean(culture || language),
  };
};

/**
 * Locale resolved from a raw event locale string, without requiring that event to be the one in
 * EventInfoProvider context. This is the context-free counterpart of {@link useEventLocaleCulture} /
 * {@link useEventLocaleCurrency}, for callers holding several events' locales at once — e.g. the
 * public Shop event picker, which lists many events and has no event context at all.
 *
 * Returns `undefined` when the event has no locale set.
 */
export const resolveEventLocale = (eventLocale: Maybe<string>): GeoBasedLocalePayload | undefined => {
  if (!eventLocale) return undefined;
  return findClosestMatchingLocale({ language: eventLocale });
};

const useEventLanguageGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const { eventInfo } = useEventInfo();
  return resolveEventLocale(eventInfo?.locale);
};

/**
 * The guest's persisted language pick (guest-site selector, PRNT-4574), as a
 * language-chain source. Only yields a value when the selector flag is on and
 * the cookie's language is still one the couple has enabled — a choice the
 * couple later disabled falls through to the event locale.
 */
const useGuestSelectedLanguageGeoLocale = (): GeoBasedLocalePayload | undefined => {
  const { eventInfo, ssrEventInfo, eventHandle, loading } = useEventInfo();
  // The cookie's Path=/<eventHandle> is a prefix of admin routes (/<handle>/edit/…), so
  // the browser sends it there too — but a guest's pick must never outrank the couple's
  // Default Event Language on admin surfaces (PRNT-4910). This source is fully inert on
  // admin: no flag reads, no staleness clearing, no pick. The pathname read is not a
  // subscription, but every consumer re-renders on navigation anyway (this hook sits
  // behind useQueryParamHelper → useLocation), so the per-render read stays fresh
  // across SPA transitions; admin SSR is covered by stripGuestLanguageCookieOnAdmin.
  const pathname = withWindow(global => global.location.pathname, undefined);
  const isAdminSurface = Boolean(pathname && isAdminRoutePath(pathname));
  // Skip the flag reads on non-wedding events (feature is wedding-only, PRNT-4604) and on
  // admin surfaces so Amplitude doesn't track exposure; skipped flags report
  // [false, resolved]. During the initial load only ssrEventInfo carries the event type.
  const isWeddingEvent = (eventInfo?.eventType ?? ssrEventInfo?.eventType) === GeneratedEventType.wedding;
  const skipFlags = !isWeddingEvent || isAdminSurface;
  const [selectorEnabled, selectorFlagLoading] = useIsFeatureEnabled('guestSiteLanguageSelectorEnabled', { skip: skipFlags });
  const [translationsEnabled, translationsFlagLoading] = useIsFeatureEnabled('joyWebTranslationsEnabled', { skip: skipFlags });
  const cookieLanguage = readGuestLanguageCookie();
  const resolvedCookieLanguage = cookieLanguage ? resolveSupportedLocale(cookieLanguage).locale : undefined;
  const enabledLanguages = getEnabledGuestLanguages(eventInfo?.locale, eventInfo?.guestDisplayLocales);
  const isCookieLanguageEnabled = Boolean(resolvedCookieLanguage && enabledLanguages.includes(resolvedCookieLanguage));
  // The picker renders only when BOTH flags are on and the event is a wedding, so a cookie is
  // honored under the same conditions — otherwise the guest would be steered by a control they
  // can no longer see.
  const isSelectorHonored = Boolean(selectorEnabled && translationsEnabled && isWeddingEvent);

  // SSR trusts the cookie before event data or flags exist (the detector cannot validate it),
  // so a pick that is no longer honored — the couple disabled the language, or either gating
  // flag was turned off (there is no picker left to undo the choice) — must be cleared here
  // so the next server render falls back to the event locale.
  // Never judge staleness on admin surfaces: flags are skipped there (reporting false),
  // so the pick would look stale and a couple visiting /edit would wipe it (PRNT-4910).
  const areFlagsResolved = !selectorFlagLoading && !translationsFlagLoading;
  const isCookieStale = Boolean(!isAdminSurface && resolvedCookieLanguage && !loading && eventInfo && areFlagsResolved && (!isSelectorHonored || !isCookieLanguageEnabled));
  useEffect(() => {
    if (isCookieStale && eventHandle) {
      removeGuestLanguageCookie(eventHandle);
    }
  }, [isCookieStale, eventHandle]);

  if (isAdminSurface || !isSelectorHonored || !isCookieLanguageEnabled) return undefined;
  return findClosestMatchingLocale({ language: resolvedCookieLanguage as string }, { preferLanguage: true });
};

const useBrowserLanguageGeoLocale = (): { forLanguage: GeoBasedLocalePayload | undefined; forCulture: GeoBasedLocalePayload | undefined } => {
  const browserLanguage = withWindow(global => global.navigator.language, undefined);

  return {
    forLanguage: findClosestMatchingLocale({ language: browserLanguage }, { preferLanguage: true }),
    forCulture: findClosestMatchingLocale({ language: browserLanguage }),
  };
};

/**
 * Adds languageCodeIso15897, country code (optional), and country name (optional) to the locale
 * @param locale The locale to extend with additional properties.
 * @param countryTranslation A translation accessor that returns localized country names keyed by ISO alpha-2 codes.
 * @returns The extended locale with additional properties.
 */
const extendLocale = (
  locale: GeoBasedLocalePayload,
  countryTranslation: GetTranslation<TranslationsByNamespace['countries']>,
  languageCultureOverride?: CultureCode,
): GeoBasedLocaleExtended => {
  // this variant is for European countries that use EUR as currency but have different cultures, we do not want then to show GB as country code
  const europeScenario = locale.culture === 'GB' && locale.currency === 'EUR';
  const countryCodeAlpha2 = (!europeScenario && validCountryAlpha2Code(locale.culture)) || undefined;
  // europeScenario will have undefined countryCodeAlpha2
  const countryName = countryCodeAlpha2 ? countryTranslation(countryCodeAlpha2)() : undefined;
  return {
    ...locale,
    languageCodeIso15897: constructLanguageCodeIso15897(locale.language, languageCultureOverride ?? locale.culture),
    countryCodeAlpha2,
    countryName,
  };
};

type GeoBasedLocaleExtended = GeoBasedLocalePayload & {
  languageCodeIso15897: string;
  countryCodeAlpha2?: string;
  countryName?: string;
};

/**
 * Resolves the currency only from explicit signals — URL param, the user's stored
 * selection, or the event's configured locale — never from the browser locale or the
 * USD default that {@link useGeoBasedLocale} always falls back to.
 *
 * Returns `undefined` when no explicit currency exists so callers can defer to
 * browser-region detection instead of being forced to USD/imperial. This matters for
 * unsupported-but-metric browser locales (e.g. `pt-BR`, `nl-NL`) that
 * `findClosestMatchingLocale` normalizes away.
 */
export const useExplicitlySelectedCurrency = (): CurrencyCodeType | undefined => {
  const { locale: searchParamLocale } = useSearchParamsGeoLocale();
  const localStorageLocale = getLocallyStoredGeoLocale();
  const eventLocale = useEventLanguageGeoLocale();

  return searchParamLocale?.currency ?? localStorageLocale?.currency ?? eventLocale?.currency;
};

interface GeoBasedLocaleOptions {
  /**
   * When true, include the user's stored language (localStorage) in the language
   * resolution chain. Enable this in create-event flows where the user may have
   * selected a language on marcom before arriving at the create form.
   *
   * Default: false — guest-site paths exclude localStorage from the language chain
   * because there is no guest-side language picker to undo a stale value.
   */
  includeStoredLanguage?: boolean;
}

export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload, options?: GeoBasedLocaleOptions) => {
  const { t } = useTranslation('countries');
  const { locale: searchParamLocale, hasRegionSignal: searchParamHasRegion } = useSearchParamsGeoLocale();
  const eventLocale = useEventLanguageGeoLocale();
  const guestSelectedLocale = useGuestSelectedLanguageGeoLocale();
  const localStorageLocale = getLocallyStoredGeoLocale();
  const { forLanguage: browserLocaleForLanguage, forCulture: browserLocaleForCulture } = useBrowserLanguageGeoLocale();

  const fallbackLocale = defaultValues ?? DefaultLocalePayload;
  const searchParamRegionLocale = searchParamHasRegion ? searchParamLocale : undefined;

  // Language and culture/currency use separate priority chains:
  //
  // Language (default):              queryParam > guestSelected > eventLocale > browser > default
  // Language (includeStoredLanguage): queryParam > guestSelected > eventLocale > localStorage > browser > default
  // Culture/currency:                queryParam > localStorage > eventLocale > browser > default
  //
  // guestSelected is the guest-site language picker's cookie (PRNT-4574): an
  // explicit per-event guest choice, so it outranks the event locale, but an
  // explicit URL param still wins. It never feeds culture/currency — picking a
  // language must not change prices or units.
  //
  // By default, localStorage is excluded from the language chain: it is
  // shared with marcom (which has a language picker that persists to
  // localStorage), and currency selection also writes the full locale blob.
  // Without a guest-site language picker, there is no way for a guest to
  // undo a stale localStorage language — so event locale and browser locale
  // are the only automatic sources for translation language.
  //
  // Create-event flows opt in via { includeStoredLanguage: true } so that a
  // user who selected Italian on marcom creates an it-IT event rather than
  // falling back to their en-US browser language.
  //
  // For culture/currency, localStorage sits ahead of eventLocale so an
  // explicit user selection (currency picker) still wins, but eventLocale
  // sits ahead of browser locale so that users with a generic en-US browser
  // see the currency matching their event's configured language
  // (e.g. an Australian couple with en-US browser + en-AU event sees AUD).
  const geoSourceLocale = searchParamRegionLocale ?? localStorageLocale ?? eventLocale ?? browserLocaleForCulture ?? fallbackLocale;
  const geoSourceWithEventLocale = options?.includeStoredLanguage
    ? (searchParamRegionLocale ?? guestSelectedLocale ?? eventLocale ?? localStorageLocale ?? browserLocaleForLanguage ?? fallbackLocale)
    : (searchParamRegionLocale ?? guestSelectedLocale ?? eventLocale ?? browserLocaleForLanguage ?? fallbackLocale);

  // The URL currency is an explicit override even without a region signal, so it still wins.
  const currency = searchParamLocale?.currency ?? geoSourceLocale.currency;
  // Culture for display (country code, country name) comes from the currency chain.
  // Culture for the language tag (languageCodeIso15897) comes from the language chain,
  // so stale localStorage culture (e.g. AU from a previous event visit) does not
  // produce en-AU translations on an en-US event.
  const locale: GeoBasedLocalePayload = { culture: geoSourceLocale.culture, currency, language: geoSourceWithEventLocale.language };

  return extendLocale(locale, t, geoSourceWithEventLocale.culture);
};

/**
 * Like {@link useGeoBasedLocale} but resolved purely from guest signals — the event's configured
 * locale (Default Event Language) is intentionally ignored. Use this where the default should follow
 * the guest's own region, not the couple's event settings (e.g. the hotel checkout phone country-code
 * picker and billing address country).
 *
 * Priority for culture/currency and language (no eventLocale in either chain):
 *
 *   queryParam > localStorage > browser > default
 *
 * Returns the same {@link GeoBasedLocaleExtended} shape as {@link useGeoBasedLocale}, so it is a drop-in
 * replacement for callers that only need guest-derived geo.
 */
export const useGuestGeoLocale = (defaultValues?: GeoBasedLocalePayload) => {
  const { t } = useTranslation('countries');
  const { locale: searchParamLocale, hasRegionSignal: searchParamHasRegion } = useSearchParamsGeoLocale();
  const localStorageLocale = getLocallyStoredGeoLocale();
  const { forLanguage: browserLocaleForLanguage, forCulture: browserLocaleForCulture } = useBrowserLanguageGeoLocale();

  const fallbackLocale = defaultValues ?? DefaultLocalePayload;

  // The URL is a culture/language source only when it actually carried a culture or region signal.
  // A currency-only URL (?currency=EUR, written by updateLocaleCurrency) resolves to a US-defaulted
  // locale; without this guard it would beat localStorage / the browser and pin a fr-FR guest who
  // picked EUR to a US phone/billing country. Its currency still overrides below.
  const searchParamRegionLocale = searchParamHasRegion ? searchParamLocale : undefined;

  const geoSourceLocale = searchParamRegionLocale ?? localStorageLocale ?? browserLocaleForCulture ?? fallbackLocale;
  const languageSourceLocale = searchParamRegionLocale ?? localStorageLocale ?? browserLocaleForLanguage ?? fallbackLocale;

  const currency = searchParamLocale?.currency ?? geoSourceLocale.currency;
  const locale: GeoBasedLocalePayload = { culture: geoSourceLocale.culture, currency, language: languageSourceLocale.language };

  return extendLocale(locale, t);
};

/**
 * Culture resolved solely from the event's configured locale (Default Event Language),
 * ignoring query params, localStorage, and the browser locale. The event locale is
 * auto-detected at creation time (venue country / IP-based signup defaults) and
 * backfilled for existing CA/AU/NZ/GB events, making it the strongest signal for
 * whether an *event* is international.
 *
 * Returns `undefined` when the event has no locale set, so callers can fall back to
 * guest-derived signals ({@link useGuestGeoLocale}).
 */
export const useEventLocaleCulture = (): CultureCode | undefined => {
  return useEventLanguageGeoLocale()?.culture;
};

/**
 * Currency resolved solely from the event's configured locale, ignoring query params,
 * localStorage, and the browser locale — the currency counterpart of
 * {@link useEventLocaleCulture}. Returns `undefined` when the event has no locale set.
 */
export const useEventLocaleCurrency = (): CurrencyCodeType | undefined => {
  return useEventLanguageGeoLocale()?.currency;
};

export const isValidCurrencyCode = (value: string): value is CurrencyCode => {
  return Object.values(CurrencyCode).includes(value as CurrencyCode);
};

export const updateLocaleCurrency = (currency: CurrencyCodeType) => {
  // Browser fallback when storage is empty — keeps a fr-FR picker on FR/fr instead of resetting to US/en.
  const baseLocale = getLocallyStoredGeoLocale() ?? getBrowserGeoLocale();
  updateLocaleLocalStorage({ ...baseLocale, currency });
  updateLocaleSearchParams({ currency }, { addParam: 'currency' });
};

// sync detected locale to i18n
export const useI18nLocaleSyncer = () => {
  const [translationsEnabled] = useIsFeatureEnabled('joyWebTranslationsEnabled');
  const { loading: eventInfoLoading } = useEventInfo();
  const locale = useGeoBasedLocale();

  const { culture, currency, language } = locale;

  useEffect(() => {
    // Never persist auto-detected locale — it would leak across events on the next visit.
    // localStorage is written only by explicit user actions (currency/language pickers).
    updateLocaleSearchParams({
      culture: locale.culture,
      currency: locale.currency,
      language: locale.languageCodeIso15897,
    });

    // Wait for eventInfo before switching so stale cached locale cannot override the SSR/event locale.
    //
    // `switchLanguage()` (like `i18n.changeLanguage()`) bypasses i18next's detection pipeline — the
    // convertDetectedLanguage closure only runs for detector-provided values.
    // Regional inputs like `fr-CA` / `de-AT` must be canonicalized here or
    // i18next will fail to find a bundle and fall back to English.
    if (translationsEnabled && !eventInfoLoading) {
      const resolved = resolveSupportedLocale(locale.languageCodeIso15897);
      if (resolved.recognized) {
        void switchLanguage(resolved.locale);
      }
    }

    // use culture, currency and language in the dependency array to prevent retriggering when the 'locale' object's
    // reference is updated, but the values are the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture, currency, language, translationsEnabled, eventInfoLoading]);
};
